import { useState, useRef, useEffect, useCallback } from 'react';
import { getPlacePredictions, getPlaceFromPrediction } from '../../services/googleMaps';
import useTripStore from '../../context/TripStore';
import useGeolocation from '../../hooks/useGeolocation';

/**
 * Full-screen search overlay for mobile
 * Shows when user taps on a search input
 */
function SearchOverlay({ isOpen, onClose, mode, onSelect }) {
  const [inputValue, setInputValue] = useState('');
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  const tripCity = useTripStore((state) => state.trip?.city);
  const setPreviewLocation = useTripStore((state) => state.setPreviewLocation);
  const { loading: geoLoading, requestLocation } = useGeolocation();

  // Focus input when overlay opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
    // Clear state when closing
    if (!isOpen) {
      setInputValue('');
      setResults([]);
      setPreviewLocation(null);
    }
  }, [isOpen, setPreviewLocation]);

  // Handle input change with debounced autocomplete
  const handleInputChange = useCallback(
    async (e) => {
      const value = e.target.value;
      setInputValue(value);

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      if (!value.trim()) {
        setResults([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      debounceRef.current = setTimeout(async () => {
        try {
          const predictions = await getPlacePredictions(value, {
            ...(tripCity?.coordinates && { location: tripCity.coordinates }),
            types: mode === 'start' ? ['establishment', 'geocode'] : ['establishment'],
          });
          setResults(predictions);
        } catch (error) {
          console.error('Autocomplete error:', error);
          setResults([]);
        }
        setIsLoading(false);
      }, 300);
    },
    [tripCity, mode]
  );

  // Handle selecting a result
  const handleSelectResult = useCallback(
    async (result) => {
      try {
        const placeDetails = await getPlaceFromPrediction(result.placeId);
        onSelect(placeDetails);
      } catch (error) {
        console.error('Error getting place details:', error);
        // Fallback to basic info
        onSelect({
          placeId: result.placeId,
          name: result.mainText,
          address: result.secondaryText,
        });
      }
      onClose();
    },
    [onSelect, onClose]
  );

  // Handle preview on hover/focus
  const handleResultFocus = useCallback(
    async (result) => {
      try {
        const placeDetails = await getPlaceFromPrediction(result.placeId);
        if (placeDetails?.coordinates) {
          setPreviewLocation({
            coordinates: placeDetails.coordinates,
            name: placeDetails.name || result.mainText,
          });
        }
      } catch (error) {
        // Silently fail
      }
    },
    [setPreviewLocation]
  );

  // Handle current location for start mode
  const handleUseCurrentLocation = useCallback(async () => {
    try {
      const coords = await requestLocation();
      if (window.google?.maps) {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: coords }, (results, status) => {
          if (status === 'OK' && results[0]) {
            const place = results[0];
            onSelect({
              placeId: place.place_id,
              name: place.formatted_address,
              address: place.formatted_address,
              coordinates: coords,
            });
            onClose();
          }
        });
      }
    } catch (error) {
      console.error('Error getting current location:', error);
    }
  }, [requestLocation, onSelect, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col overflow-hidden">
      {/* Header with search input */}
      <div className="flex items-center gap-2 p-3 border-b border-gray-200">
        {/* Back button */}
        <button
          type="button"
          onClick={onClose}
          className="p-1.5 -ml-1 text-gray-600 hover:text-gray-900 rounded-full hover:bg-gray-100"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        {/* Search input */}
        <div className="flex-1 relative">
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            placeholder={mode === 'start' ? 'Search starting location...' : 'Search places to visit...'}
            className="w-full py-2.5 px-3 bg-gray-100 rounded-lg text-sm text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-colors"
            autoComplete="off"
          />
          {inputValue && (
            <button
              type="button"
              onClick={() => {
                setInputValue('');
                setResults([]);
                inputRef.current?.focus();
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Results list */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        {/* Current location option for start mode */}
        {mode === 'start' && !inputValue && (
          <button
            type="button"
            onClick={handleUseCurrentLocation}
            disabled={geoLoading}
            className="w-full px-3 py-3 flex items-center gap-3 hover:bg-gray-50 active:bg-gray-100 border-b border-gray-100"
          >
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              {geoLoading ? (
                <svg className="w-4 h-4 text-blue-600 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              ) : (
                <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              )}
            </div>
            <div className="flex-1 text-left">
              <p className="text-sm font-medium text-blue-600">Use current location</p>
              <p className="text-xs text-gray-500">Find places near you</p>
            </div>
          </button>
        )}

        {/* Loading state */}
        {isLoading && (
          <div className="px-4 py-6 text-center">
            <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" />
            <p className="mt-2 text-xs text-gray-500">Searching...</p>
          </div>
        )}

        {/* Empty state */}
        {!isLoading && inputValue && results.length === 0 && (
          <div className="px-4 py-6 text-center text-gray-500 text-sm">
            <p>No results found for "{inputValue}"</p>
          </div>
        )}

        {/* Results */}
        {!isLoading && results.map((result, index) => (
          <button
            key={result.placeId || index}
            onClick={() => handleSelectResult(result)}
            onMouseEnter={() => handleResultFocus(result)}
            onFocus={() => handleResultFocus(result)}
            className="w-full px-3 py-2.5 flex items-center gap-3 hover:bg-gray-50 active:bg-gray-100 border-b border-gray-100 text-left"
          >
            {/* Location icon */}
            <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>

            {/* Place info */}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{result.mainText}</p>
              {result.secondaryText && (
                <p className="text-xs text-gray-500 truncate">{result.secondaryText}</p>
              )}
            </div>

            {/* Plus icon to add */}
            <div className="flex-shrink-0 w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

export default SearchOverlay;
